import { CheckCircle } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { QuestionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { DueDiligenceAnswerType, DueDiligenceStatusColor } from 'shared/types/due-diligence';

interface WizardStep4Props {
  question: QuestionItem;
}

const ANSWER_TYPE_LABELS: Record<DueDiligenceAnswerType, string> = {
  [DueDiligenceAnswerType.OPTIONS]: 'Status vraag',
  [DueDiligenceAnswerType.SIMPLE_SELECT]: 'Selectie vraag',
  [DueDiligenceAnswerType.DOCUMENT_EXISTS]: 'Document aanwezig',
  [DueDiligenceAnswerType.EXTRACTION]: 'Extractie',
  [DueDiligenceAnswerType.FREE_TEXT]: 'Vrije tekst',
  [DueDiligenceAnswerType.DATE]: 'Datum',
  [DueDiligenceAnswerType.NUMBER]: 'Nummer',
  [DueDiligenceAnswerType.BOOLEAN]: 'Ja/Nee',
};

function getStatusColorDot(color: DueDiligenceStatusColor) {
  const colorMap = {
    [DueDiligenceStatusColor.GREEN]: 'bg-green-500',
    [DueDiligenceStatusColor.ORANGE]: 'bg-orange-500',
    [DueDiligenceStatusColor.RED]: 'bg-red-500',
    [DueDiligenceStatusColor.TRANSPARENT]: 'bg-gray-300',
  };
  return colorMap[color] || 'bg-gray-300';
}

function countConditions(conditionRoot: any): number {
  if (!conditionRoot) return 0;

  let count = 0;
  if (conditionRoot.conditions) {
    count += conditionRoot.conditions.length;
  }
  if (conditionRoot.groups) {
    conditionRoot.groups.forEach((group: any) => {
      count += countConditions(group);
    });
  }
  return count;
}

export function WizardStep4Review({ question }: WizardStep4Props) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <CheckCircle weight="fill" className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Klaar om op te slaan!</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Controleer de configuratie hieronder voordat je de vraag toevoegt.
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          {/* Question name and field name */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Vraagnaam</label>
            <p className="text-sm font-medium text-foreground mt-1">{question.displayName}</p>
            {question.fieldName && (
              <p className="text-xs text-muted-foreground mt-1">
                <code className="bg-muted px-1 py-0.5 rounded">{question.fieldName}</code>
              </p>
            )}
          </div>

          {/* Answer type */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Antwoordtype</label>
            <div className="mt-1">
              <Badge variant="secondary">{ANSWER_TYPE_LABELS[question.answerType]}</Badge>
            </div>
          </div>

          {/* Help text */}
          {question.helpText && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Help tekst</label>
              <p className="text-sm text-foreground mt-1">{question.helpText}</p>
            </div>
          )}

          {/* Answer options count */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Antwoordopties</label>
            <p className="text-sm text-foreground mt-1">
              {question.answerOptions.length} {question.answerOptions.length === 1 ? 'optie' : 'opties'}
            </p>
          </div>

          {/* Answer options details */}
          {question.answerOptions.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              {question.answerOptions.map((option) => {
                const conditionCount = countConditions(option.conditionRoot);

                return (
                  <div key={option.id || option.value} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <div className={`w-3 h-3 rounded-full ${getStatusColorDot(option.statusColor)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {option.label}
                        {option.isDefault && (
                          <Badge variant="outline" className="ml-2 text-xs">Standaard</Badge>
                        )}
                      </p>
                      {conditionCount > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {conditionCount} {conditionCount === 1 ? 'voorwaarde' : 'voorwaarden'}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
